import { cn } from '@/lib/utils';
import {
  ArrowRightIcon,
  BotIcon,
  ClockIcon,
  FileTextIcon,
  HashIcon,
  HistoryIcon,
  LayersIcon,
  MapPinIcon,
  SparklesIcon,
  TagIcon,
  UserRoundCheckIcon,
  UserRoundIcon,
  Workflow as WorkflowIcon,
  PaperclipIcon,
} from 'lucide-react';
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';

import { Badge } from '@/components/ui/badge';
import { CopyButton } from '@/shared/components/CopyButton';
import { PersonName } from '@/shared/components/PersonName';
import { ViaTool } from '@/features/editor-access';
import { NeedsAccountReason, useAssignablePeople } from '@/features/identity';
import {
  LabelPicker,
  TaskLabelRow,
  useCreateLabel,
  useReplaceTaskLabels,
} from '@/features/labels';
import { useWorkspace } from '@/features/workspaces';
import { useCanWork } from '@/features/workspaces/canWork';
import { EyebrowLabel } from '@/shared/components/EyebrowLabel';
import { formatExactTime, formatRelativeTime } from '@/shared/utils/time';
import {
  BLOCKED_SCOPE_LABEL,
  deriveCardExecutionState,
  displayModel,
  newestFirst,
} from '../executionStatus';
import { runOutcome } from '../executionHistory';
import {
  useClearStepAssignee,
  useSetStepAssignee,
} from '../hooks/useTaskStepAssignee';
import { TaskAttachmentsSection } from './TaskAttachmentsSection';
import { GateAssigneeField } from './GateAssigneeField';
import { TaskDescriptionField } from './TaskDescriptionField';
import { TaskTransitions } from './TaskTransitions';
import type { WaitingReason } from '../executionStatus';
import { phaseRepeatsStep, type TaskLocation } from '../locateTask';
import type {
  LabelView,
  TaskLabelView,
  TaskView,
  UserSummary,
} from '@/lib/api/generated/model';
import { notStartedCount } from '../linkedChildren';
import { StartAllChildren } from './LinkedChildrenActions';
import { SchedulerStatus } from './SchedulerStatus';
import { LinkedChildrenList } from './LinkedChildrenList';

/**
 * Why a task is waiting, in a sentence. The board shows one status for both
 * reasons because a person scanning it cannot be asked to tell a pause from a
 * branch — but here there is room to say which, and "Human Input Needed"
 * means nothing until you know what input.
 */
const WAITING_REASONS: Record<WaitingReason, string> = {
  'human-gate':
    'This step needs a decision from a person before it can continue.',
  branching:
    'This step offers more than one way forward, so someone has to choose which path this task takes.',
};

/**
 * One labelled fact, in the shape the workflow builder's step inspector
 * already uses (`GraphDetailPanel`'s `Field`): an iconned label above its
 * value, with no box around it.
 *
 * This panel and that one describe the same four things — phase, model,
 * prompt, the paths onward — about two halves of the same workflow. When they
 * were drawn differently, someone who inspected a step in the builder and then
 * opened a task on the board met two products. Six bordered cards was also
 * simply more border than six one-line values can earn.
 */
function Field({
  label,
  icon: Icon,
  children,
  wide = false,
}: {
  label: string;
  icon: typeof LayersIcon;
  children: ReactNode;
  /** Spans both columns, for a value no half-width column can hold. */
  wide?: boolean;
}) {
  return (
    <div className={`flex flex-col gap-0.5 ${wide ? 'col-span-2' : ''}`}>
      <EyebrowLabel icon={Icon}>{label}</EyebrowLabel>
      <span className="text-foreground text-sm">{children}</span>
    </div>
  );
}

/**
 * Who created the task, and through which AI tool when it was one (spec 246).
 * The field is half the panel wide, so the tool wraps under the name rather
 * than cutting it.
 */
function CreatedBy({
  person,
  via,
}: {
  person: UserSummary;
  via: string | null | undefined;
}) {
  return (
    <span className="inline-flex max-w-full flex-wrap items-baseline gap-x-1">
      <PersonName person={person} />
      <ViaTool via={via} />
    </span>
  );
}

/**
 * A model by its name, without the release date some identifiers carry
 * (`claude-haiku-4-5-20251001` reads as `claude-haiku-4-5`); the whole
 * identifier is on hover (spec 181).
 */
function ModelName({ model }: { model: string | null | undefined }) {
  if (!model)
    return <span className="text-muted-foreground">None declared</span>;
  const name = model.replace(/-\d{8}$/, '');
  return <span title={model}>{name}</span>;
}

/** A titled region. Same label treatment as a field, because it is the same role. */
function Section({
  title,
  icon: Icon,
  children,
  className,
}: {
  title: string;
  icon: typeof LayersIcon;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('flex flex-col gap-1.5', className)}>
      <EyebrowLabel icon={Icon}>{title}</EyebrowLabel>
      {children}
    </section>
  );
}

/** A moment, written the way this product writes moments, with the exact value kept on the element. */
function When({ at }: { at: string }) {
  return <time title={formatExactTime(at)}>{formatRelativeTime(at)}</time>;
}

export interface TaskDetailsPanelProps {
  workspaceId: string;
  /**
   * This task's *own* workflow key — never the target workflow's (FR-004,
   * spec 115) — so the "Created tasks" header can link to the run page
   * (`.../workflows/$workflowKey/tasks/$taskId/run`). Optional so a caller
   * that has not resolved it yet (or a task off any board) still renders the
   * plain, non-interactive header rather than a broken `Link`.
   */
  workflowKey?: string;
  task: TaskView;
  /**
   * Where the board placed this task. `undefined` when no step holds it —
   * a stored position can name a step the workflow no longer declares.
   */
  location: TaskLocation | undefined;
  /**
   * Every step on this board by id, so a transition can be labelled by the step
   * it runs rather than by the identifier it points at.
   */
  stepLabels: Map<string, string>;
  /**
   * Narrow the board to a label the reader pressed on this task (D7). Absent on
   * a surface that has no narrowing of its own to change.
   */
  onNarrowToLabel?: (labelId: string) => void;
  /**
   * The workspace's whole label vocabulary, so the picker can offer words no
   * task carries yet. Passed in for the same reason the task is: the board
   * already holds the aggregate, and a second read here could serve a
   * different snapshot mid-poll.
   */
  labelRegistry: readonly LabelView[];
  /**
   * The description being typed, and the way to change it. Owned by the drawer
   * so a tab switch does not destroy an unsaved draft; `null` means nobody is
   * editing.
   */
  descriptionDraft: string | null;
  onDescriptionDraftChange: (draft: string | null) => void;
}

/**
 * What this task is, where it is, and what it can do next.
 *
 * Derives from the same function and the same step object the card behind it
 * used, rather than reading the task again. Two surfaces describing one task
 * from two derivations would eventually disagree, and the one place a person
 * goes to resolve a disagreement is this panel.
 *
 * The status *name* is not repeated here — the drawer header carries it, where
 * it stays readable from every tab. What this panel adds is the part a chip
 * cannot say: which kind of wait a wait is, and what a failure said.
 *
 * A task the board could not place gets none of the step-derived facts. Every
 * one of them — phase, step, kind, model, prompt, the paths out — is read
 * through a step that, for this task, does not exist. What it gets instead is
 * its stored position verbatim: for a task orphaned by an edit to its workflow,
 * that string is the only surviving clue to what the workflow used to look like,
 * and six em-dashes are not a substitute for it.
 */
export function TaskDetailsPanel({
  workspaceId,
  workflowKey,
  task,
  location,
  stepLabels,
  labelRegistry,
  onNarrowToLabel,
  descriptionDraft,
  onDescriptionDraftChange,
}: TaskDetailsPanelProps) {
  const state = deriveCardExecutionState(task, location?.step);
  const step = location?.step;
  const isGate = step?.kind === 'human_gate';
  const isLinkedWorkflowJoin =
    step?.kind === 'linked_workflow' && task.linked_tasks != null;

  // The workspace aggregate is already the one server read this drawer's
  // surroundings pull from (`useWorkspace`, spec D-10) — reused here to
  // resolve the step's `workflow` key to a display name rather than adding a
  // second read of the same object.
  const workspace = useWorkspace(workspaceId);
  const canWork = useCanWork();
  const targetWorkflowName =
    workspace.data?.workflows.find(
      (candidate) => candidate.key === step?.workflow,
    )?.name ?? null;

  // The newest `linked_workflow` run once its join has closed — the parent has
  // moved on, `linked_tasks` is cleared, and without this the drawer would
  // hold no trace of the fan-out it came through. While a join is open, the
  // section below lists the created tasks (spec 130), and the run page holds
  // the full view.
  const lastLinkedRun = isLinkedWorkflowJoin
    ? undefined
    : newestFirst(task.executions ?? []).find(
        (run) => run.step_kind_snapshot === 'linked_workflow',
      );

  const replaceLabels = useReplaceTaskLabels();
  const createLabel = useCreateLabel();

  // Asked for only so this panel can offer the override; the same hook the
  // builder's step inspector uses, so a colleague provisioned minutes ago is
  // selectable in both without either waiting for them to sign in.
  const {
    people,
    failed: directoryFailed,
    loading: peopleLoading,
  } = useAssignablePeople(workspaceId);
  const setAssignee = useSetStepAssignee();
  const clearAssignee = useClearStepAssignee();
  // Whom the workflow itself names here. Read off the board's own step section
  // rather than looked up again, so the "follow the workflow" wording and the
  // optimistic patch cannot describe a different person than the column does.
  const definitionAssignee = step?.assignee ?? null;
  const assignmentPending = setAssignee.isPending || clearAssignee.isPending;

  // A label created from the picker is selected in the same gesture, well
  // before the invalidated aggregate comes back carrying it. Remembering it
  // here is what lets the optimistic chip show its name instead of appearing
  // as a gap until the refetch lands.
  const [created, setCreated] = useState<LabelView[]>([]);
  /*
    The same just-created labels, held in a ref as well as in state.

    `onChange` fires immediately after `onCreate` resolves, in the same tick, so
    the `applyLabels` it calls is still the one from the render *before*
    `setCreated` — whose `vocabulary` does not yet contain the label that was
    just made. Reading the ref instead is what lets the optimistic chip carry the
    new label's name straight away, which is the entire reason this state exists.
    The state is still what re-renders the picker's registry; the ref is only for
    reads that happen before it flushes.
  */
  const createdRef = useRef<LabelView[]>([]);
  const vocabulary = useMemo(() => {
    const byId = new Map<string, LabelView>();
    for (const label of [...labelRegistry, ...created])
      byId.set(label.id, label);
    return byId;
  }, [labelRegistry, created]);

  function lookUp(id: string): LabelView | undefined {
    return (
      vocabulary.get(id) ?? createdRef.current.find((label) => label.id === id)
    );
  }

  const labels = task.labels ?? [];
  const selectedIds = labels.map((label) => label.id);

  // What the chips should read while the write is in flight. Built from the
  // vocabulary rather than from the task, because the ids that just changed are
  // exactly the ones the task does not carry yet. An id in neither the registry
  // nor the just-created set is dropped: it is a label this workspace does not
  // have, and inventing a name for it would be the one thing worse than a
  // missing chip.
  function optimisticLabels(ids: readonly string[]): TaskLabelView[] {
    return ids.flatMap((id) => {
      const label = lookUp(id);
      return label
        ? [
            {
              id: label.id,
              name: label.name,
              color: label.color,
              actor_type: 'user' as const,
            },
          ]
        : [];
    });
  }

  function applyLabels(ids: string[]) {
    replaceLabels.mutate({
      workspaceId,
      taskId: task.id,
      labelIds: ids,
      optimistic: optimisticLabels(ids),
    });
  }

  /*
    The open join: one line with the count and the run page, then the created
    tasks, what needs someone first (spec 130 FR-008). Spec 101 once moved that
    list to the run page alone; it came back on request, because a failed child
    should be named where its parent is read. While the task stands on the join
    it is the first thing in the drawer (spec 140 US6): it is what the task is
    waiting on, and it used to sit below attachments and identifiers.
  */
  const toStart = notStartedCount(task.linked_tasks);
  const childrenSection =
    isLinkedWorkflowJoin && task.linked_tasks ? (
      // `#children` is where a "ready to start" notification lands (spec 140 US3).
      <div id="children" className="scroll-mt-4">
        <Section title="Sub-workflow run" icon={WorkflowIcon}>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <span className="text-foreground">
              {targetWorkflowName ?? 'Unresolved workflow'}
            </span>
            <span className="text-muted-foreground text-xs">
              {task.linked_tasks.done}/{task.linked_tasks.total} done
            </span>
            {workflowKey ? (
              <Link
                to="/workspaces/$workspaceId/workflows/$workflowKey/tasks/$taskId/run"
                params={{ workspaceId, workflowKey, taskId: task.id }}
                className="text-foreground ml-auto text-xs underline underline-offset-4"
              >
                Open run →
              </Link>
            ) : null}
          </div>
          {/* What the scheduler is doing with the waiting tasks (spec 239 US1). */}
          <SchedulerStatus scheduler={task.linked_tasks.scheduler} />
          {/* Every waiting task started at once (spec 140 US2). */}
          {toStart > 0 ? (
            <div className="mt-1">
              <StartAllChildren
                workspaceId={workspaceId}
                task={task}
                count={toStart}
              />
            </div>
          ) : null}
          {/*
            The created tasks, what needs someone first (spec 130 FR-008).
            Spec 101 left them to the run page; the drawer listing them again
            was asked for on 2026-10-05, so a failed child is named where the
            parent is being read. Every child lives in the step's target
            workflow, so that is the board each row opens on.
          */}
          <LinkedChildrenList
            workspaceId={workspaceId}
            parent={{ id: task.id, title: task.title }}
            children={task.linked_tasks.tasks ?? []}
            childWorkflowKey={step?.workflow ?? undefined}
            selectable
          />
        </Section>
      </div>
    ) : null;

  return (
    <div className="flex flex-col gap-5">
      {childrenSection}

      {/*
        A label over "there is no description" is a heading for an absence. The
        sentence alone carries it; the label returns with the text it belongs to
        — and with the field, once one is open over it.
      */}
      <TaskDescriptionField
        workspaceId={workspaceId}
        taskId={task.id}
        description={task.description}
        draft={descriptionDraft}
        onDraftChange={onDescriptionDraftChange}
      />

      {/*
        Directly under the description, where what a task is *about* belongs —
        above where it stands and long above its identifiers. This is the one
        surface that can change a task's labels; the card behind it only
        reports them.
      */}
      <Section title="Labels" icon={TagIcon}>
        <div className="flex flex-col gap-2">
          {/*
            The chips, then a small "Add label" at the end of their row (spec
            181): an always-empty search box under them read as a second,
            blank field. The one surface where a chip acts (D7): pressing one
            narrows the board to it; the `×` takes the label off this task.
          */}
          <div className="flex flex-wrap items-center gap-1.5">
            <TaskLabelRow
              labels={labels}
              onNarrow={(labelId) => onNarrowToLabel?.(labelId)}
              onRemove={
                canWork
                  ? (labelId) => {
                      applyLabels(selectedIds.filter((id) => id !== labelId));
                    }
                  : undefined
              }
            />
            {canWork ? (
              <LabelPicker
                id="task-detail-labels"
                trigger="button"
                workspaceId={workspaceId}
                registry={[...vocabulary.values()]}
                selected={selectedIds}
                onChange={applyLabels}
                onCreate={async (name) => {
                  const label = await createLabel.mutateAsync({
                    workspaceId,
                    name,
                  });
                  // Ref first, synchronously: `onChange` runs before the state
                  // update flushes and has to be able to see this label.
                  createdRef.current = [...createdRef.current, label];
                  setCreated((prev) => [...prev, label]);
                  return label;
                }}
              />
            ) : labels.length === 0 ? (
              <p className="text-muted-foreground text-sm">No labels.</p>
            ) : null}
          </div>
          {replaceLabels.isError ? (
            <p role="alert" className="text-destructive text-xs">
              The labels could not be saved. They have been put back as they
              were.
            </p>
          ) : null}
        </div>
      </Section>

      {step ? (
        // The step's kind, set like every other fact here (spec 181): an
        // eyebrow, then the value. As a bold line of body text it read as a
        // heading; as a bordered tag beside the label chip and the "Needs
        // review" badge it read as one more badge.
        <div className="flex flex-col gap-0.5">
          <EyebrowLabel
            icon={
              isGate
                ? UserRoundCheckIcon
                : step.kind === 'linked_workflow'
                  ? WorkflowIcon
                  : BotIcon
            }
          >
            Step type
          </EyebrowLabel>
          {/* A linked step hands work to another workflow; "AI Step" said
              otherwise (spec 140 US6). */}
          <span className="text-foreground text-sm">
            {isGate
              ? 'Human Gate'
              : step.kind === 'linked_workflow'
                ? 'Linked workflow'
                : 'AI Step'}
          </span>
        </div>
      ) : null}

      {/*
        Same two backend-computed flags the card shows (spec 101 US4), same
        treatment — `Badge` with the existing state tokens, not a second
        styling for one status. Rendered as-is when both are true: this panel
        has no more precedence to apply than the card does (FR-009).
      */}
      {task.needs_attention || task.needs_review ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {task.needs_attention ? (
            <Badge
              variant="outline"
              className="text-board-state-failed border-board-state-failed/40 w-fit shrink-0"
            >
              Needs attention
            </Badge>
          ) : null}
          {task.needs_review ? (
            <Badge
              variant="outline"
              className="text-board-state-waiting border-board-state-waiting/40 w-fit shrink-0"
            >
              Needs review
            </Badge>
          ) : null}
        </div>
      ) : null}

      {/*
        Only on a gate, and only under the row that just said this is one. An
        AI step routes to nobody, and the backend refuses an override on one
        (409) — offering the control there would be an invitation to an error.
      */}
      {isGate && step ? (
        <Section title="Assignee" icon={UserRoundCheckIcon}>
          <div className="flex flex-col gap-2">
            {/*
              The person and where they come from, in the field itself (spec
              180): it was an always-empty search box with the choice in a grey
              line beneath it and a second line repeating the state.
            */}
            <GateAssigneeField
              people={people}
              peopleLoading={peopleLoading}
              directoryUnavailable={directoryFailed}
              assignee={task.assignee ?? null}
              isOverride={task.assignee_is_override ?? false}
              workflowAssignee={definitionAssignee}
              disabled={assignmentPending || !canWork}
              onUseWorkflow={() => {
                /*
                  Clears the override rather than assigning nobody: a cleared
                  gate follows the workflow, which may well name somebody
                  (FR-032).
                */
                clearAssignee.mutate({
                  workspaceId,
                  taskId: task.id,
                  stepId: step.stepId,
                  definitionAssignee,
                });
              }}
              onAssign={(person) => {
                setAssignee.mutate({
                  workspaceId,
                  taskId: task.id,
                  stepId: step.stepId,
                  assigneeUserId: person.id,
                  person,
                });
              }}
            />
            {(setAssignee.isError || clearAssignee.isError) && (
              <p role="alert" className="text-destructive text-xs">
                The assignee could not be saved. It has been put back as it was.
              </p>
            )}
          </div>
        </Section>
      ) : null}

      {/*
        What people gave the task (spec 120): the files its runs are told about,
        before the facts about where it stands.
      */}
      <Section title="Attachments" icon={PaperclipIcon}>
        <TaskAttachmentsSection workspaceId={workspaceId} taskId={task.id} />
      </Section>

      {/*
        Two columns rather than the builder's single flow: this panel carries
        six facts to its two, and stacking six labelled rows pushes the paths
        onward — the only thing here anyone can act on — below the fold.
      */}
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
        {location ? (
          <>
            {/* A phase named after its step is one place, not two (spec 181). */}
            {phaseRepeatsStep(location) ? null : (
              <Field label="Phase" icon={LayersIcon}>
                {location.phaseLabel}
              </Field>
            )}
            <Field label="Step" icon={MapPinIcon}>
              {location.stepLabel}
            </Field>
            <Field label="Model" icon={SparklesIcon}>
              <ModelName
                model={displayModel(step?.stepId, task.executions ?? [])}
              />
            </Field>
            <Field label="Created" icon={ClockIcon}>
              <When at={task.created_at} />
            </Field>
            <Field label="Created by" icon={UserRoundIcon}>
              {task.created_by ? (
                <CreatedBy person={task.created_by} via={task.created_via} />
              ) : (
                // Records written before authentication existed point at the
                // pre-authentication placeholder, which resolves to a name — so
                // reaching here means the person could not be resolved at all.
                <span className="text-muted-foreground">Unknown</span>
              )}
            </Field>
            <Field label="Updated" icon={HistoryIcon}>
              <When at={task.updated_at} />
            </Field>
          </>
        ) : (
          <>
            <Field label="Stored position" icon={MapPinIcon} wide>
              <span className="font-mono text-xs break-all">
                {task.position}
              </span>
            </Field>
            <Field label="Created" icon={ClockIcon}>
              <When at={task.created_at} />
            </Field>
            <Field label="Created by" icon={UserRoundIcon}>
              {task.created_by ? (
                <CreatedBy person={task.created_by} via={task.created_via} />
              ) : (
                // Records written before authentication existed point at the
                // pre-authentication placeholder, which resolves to a name — so
                // reaching here means the person could not be resolved at all.
                <span className="text-muted-foreground">Unknown</span>
              )}
            </Field>
            <Field label="Updated" icon={HistoryIcon}>
              <When at={task.updated_at} />
            </Field>
          </>
        )}

        {/*
          The identifier, at the bottom where a reference belongs rather than
          stacked under the title as a fourth header line. It is what you quote
          in a bug report and never what you read to know which task this is.
        */}
        <Field label="Task" icon={HashIcon} wide>
          {/* Quiet, with a way to copy it (spec 181): it is quoted, not read. */}
          <span className="text-muted-foreground flex items-center gap-1">
            <span className="font-mono text-xs break-all">{task.id}</span>
            <CopyButton
              text={task.id}
              label="Copy task id"
              copiedLabel="Task id copied"
              failedLabel="Task id not copied"
              className="-my-1 size-6"
            />
          </span>
        </Field>
      </dl>

      {!location ? (
        <p className="text-muted-foreground text-sm">
          This task is not on any step of this board.
        </p>
      ) : null}

      {/*
        `idle` is the one state with no presentation of its own — the header's
        indicator renders nothing for it, by design, because "idle" is not news.
        Left unsaid entirely it would read as a drawer that failed to load a
        status, so the panel says the quiet thing instead: nothing is happening,
        and here is where it is standing.
      */}
      {state.kind === 'idle' && location ? (
        <p className="text-muted-foreground text-sm">
          Resting on {location.stepLabel}.
        </p>
      ) : null}

      {state.kind === 'waiting-human' ? (
        <p className="text-foreground text-sm">
          {WAITING_REASONS[state.reason]}
        </p>
      ) : null}

      {state.kind === 'failed' ? (
        <div className="border-board-state-failed/40 bg-muted/30 flex flex-col gap-1 rounded-md border p-3">
          <p className="text-muted-foreground text-xs font-medium">
            Failed on:{' '}
            <span className="text-foreground">{state.stepLabel}</span>
          </p>
          <p className="text-foreground text-sm">{state.message}</p>
        </div>
      ) : null}

      {/*
        A block reads like the failure block and is deliberately not the same colour
        (spec 072 FR-023). The distinction the reader needs is "who fixes this": a
        failure is investigated here, a block is released by an administrator raising a
        limit. Same shape so the drawer stays one system; different border so the two are
        not mistaken at a glance.

        `state.message` is the service's own sentence, which names a spend limit and the
        administrator to contact and carries no figure — a member may see neither the
        limit nor the spend. Nothing here appends one.
      */}
      {state.kind === 'blocked' ? (
        <div className="border-board-state-waiting/40 bg-muted/30 flex flex-col gap-1 rounded-md border p-3">
          <p className="text-muted-foreground text-xs font-medium">
            Stopped on:{' '}
            <span className="text-foreground">{state.stepLabel}</span>
          </p>
          {/*
            Which budget refused it (spec 080 FR-019), from the service's value rather
            than from the sentence below. Releasing a workspace cap and releasing one
            workflow's cap are different requests to the same administrator.
          */}
          {state.scope !== null ? (
            <p className="text-muted-foreground text-xs font-medium">
              Stopped by:{' '}
              <span className="text-foreground">
                {BLOCKED_SCOPE_LABEL[state.scope]}
              </span>
            </p>
          ) : null}
          <p className="text-foreground text-sm">{state.message}</p>
        </div>
      ) : null}

      {/*
        Blocked for want of its person's own account (spec 160): the same shape as a
        spend block, and never its words — the fix is that person linking, not an
        administrator raising a limit, so only they are offered the way.
      */}
      {state.kind === 'needs-account' ? (
        <div className="border-board-state-waiting/40 bg-muted/30 flex flex-col gap-1 rounded-md border p-3">
          <p className="text-muted-foreground text-xs font-medium">
            Stopped on:{' '}
            <span className="text-foreground">{state.stepLabel}</span>
          </p>
          <NeedsAccountReason
            hosts={state.hosts}
            personId={state.personId}
            personName={state.personName}
            className="text-foreground text-sm"
          />
        </div>
      ) : null}

      {step ? (
        // First on a phone (spec 270): last, it sat under six fields of
        // metadata, a screen and a half below the tab, for the one thing a
        // task waiting on you asks of you.
        <Section
          title="Next step"
          icon={ArrowRightIcon}
          className="max-md:order-first"
        >
          <TaskTransitions
            workspaceId={workspaceId}
            task={task}
            step={step}
            stepLabels={stepLabels}
          />
        </Section>
      ) : null}

      {lastLinkedRun ? (
        <Section title="Sub-workflow run" icon={WorkflowIcon}>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <span className="text-foreground">
              {stepLabels.get(lastLinkedRun.step_id_snapshot) ??
                lastLinkedRun.step_id_snapshot}
            </span>
            <span className="text-muted-foreground text-xs">
              {lastLinkedRun.status === 'awaiting_children'
                ? 'Closed'
                : runOutcome(lastLinkedRun.status).label}
              {lastLinkedRun.ended_at
                ? ` ${formatRelativeTime(lastLinkedRun.ended_at)}`
                : ''}
            </span>
            {workflowKey ? (
              <Link
                to="/workspaces/$workspaceId/workflows/$workflowKey/tasks/$taskId/run"
                params={{ workspaceId, workflowKey, taskId: task.id }}
                search={{ execution: lastLinkedRun.id }}
                className="text-foreground ml-auto text-xs underline underline-offset-4"
              >
                Open run →
              </Link>
            ) : null}
          </div>
        </Section>
      ) : null}

      {/*
        The prompt belongs to the step the task is *standing on*, and is
        labelled as such. It is the workflow as it stands now — never presented
        beside a recorded run, which carries no prompt of its own and would be
        given words it never used.

        Collapsed, where the builder's inspector shows it outright, and the
        difference is deliberate: there the prompt is the subject of the panel,
        here the task is, and the step's instructions are context behind it.
        The block itself is styled exactly as the builder styles it, so opening
        this one lands somewhere familiar.
      */}
      {step?.prompt ? (
        <details className="flex flex-col gap-1.5">
          <EyebrowLabel as="summary" icon={FileTextIcon}>
            Prompt for {location?.stepLabel ?? 'this step'}
          </EyebrowLabel>
          <pre className="scrollbar-slim bg-muted/40 border-border text-foreground mt-1.5 max-h-64 overflow-auto rounded-md border p-3 font-mono text-xs whitespace-pre-wrap [overflow-wrap:anywhere]">
            {step.prompt}
          </pre>
        </details>
      ) : null}
    </div>
  );
}
